import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, MenuItem, Button, Snackbar, Alert, CircularProgress
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { getGroupDetail, getLanguagesList, getGroupLanguage, setGroupLanguage } from '../../services/api';
import Loader from '../../components/Common/Loader';

const GroupLanguage = () => {
  const { groupId } = useParams();

  const [group, setGroup] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [defaultLanguageName, setDefaultLanguageName] = useState('');
  const [activeLanguageId, setActiveLanguageId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [groupRes, languagesRes, langRes] = await Promise.all([
        getGroupDetail(groupId),
        getLanguagesList(),
        getGroupLanguage(groupId)
      ]);
      if (groupRes.success) setGroup(groupRes.data);
      if (languagesRes.success) setLanguages(languagesRes.data);
      if (langRes.success) {
        setDefaultLanguageName(langRes.data.defaultLanguageName || 'Not set');
        setActiveLanguageId(langRes.data.activeLanguageId || '');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load language settings');
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!activeLanguageId) {
      setError('Select a language first');
      return;
    }
    setSaving(true);
    try {
      await setGroupLanguage(groupId, activeLanguageId);
      setSuccess('Active language saved');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save active language');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} gutterBottom>Language</Typography>
      {group && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {group.name_english}{group.country_name ? ` — ${group.country_name}` : ''}
        </Typography>
      )}

      <Paper sx={{ p: 3, maxWidth: 520 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
            Default Language:
          </Typography>
          <Typography variant="body2">{defaultLanguageName}</Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
            Active Language:
          </Typography>
          <TextField
            select
            size="small"
            value={activeLanguageId}
            onChange={(e) => setActiveLanguageId(e.target.value)}
            sx={{ minWidth: 220 }}
            SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 320 } } } }}
          >
            {languages.map((lang) => (
              <MenuItem key={lang.id} value={lang.id}>{lang.name}</MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            startIcon={saving ? null : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </Box>
      </Paper>

      <Snackbar
        open={!!success || !!error}
        autoHideDuration={5000}
        onClose={() => { setSuccess(''); setError(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => { setSuccess(''); setError(''); }}
          severity={success ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {success || error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GroupLanguage;
